import { z } from 'zod';

// ISO 3166-1 alpha-2 (2 lettere maiuscole)
const countryCodeSchema = z
  .string()
  .length(2, 'Codice paese deve essere 2 lettere ISO')
  .regex(/^[A-Z]{2}$/, 'Codice paese deve essere maiuscolo (es. IT, CZ, DE)');

// VAT number: solo controllo FORMATO (lunghezza, alfanumerico). NO VIES check qui
// (la verifica VIES SOAP verra' implementata nel modulo Fatture, contratto C12).
const vatNumberSchema = z
  .string()
  .min(4, 'Partita IVA troppo corta')
  .max(20, 'Partita IVA max 20 caratteri')
  .regex(/^[A-Z0-9]+$/i, 'Partita IVA: solo lettere e numeri');

// Address JSON: struttura tipata ma tutti i campi opzionali per flessibilita'
export const addressSchema = z
  .object({
    street: z.string().max(200).optional(),
    city: z.string().max(100).optional(),
    postalCode: z.string().max(20).optional(),
    province: z.string().max(50).optional(),
    country: countryCodeSchema.optional(),
  })
  .partial();
export type Address = z.infer<typeof addressSchema>;

export const customerCreateRequestSchema = z.object({
  code: z
    .string()
    .min(1, 'Codice obbligatorio')
    .max(64, 'Codice max 64 caratteri')
    .regex(/^[A-Z0-9_-]+$/i, 'Codice puo contenere solo lettere, numeri, _ e -'),
  name: z.string().min(1, 'Nome obbligatorio').max(200),
  country: countryCodeSchema,
  vatNumber: vatNumberSchema.nullable().optional(),
  address: addressSchema.nullable().optional(),
  email: z.string().email('Email non valida').max(200).nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  paymentTerms: z.string().max(100).nullable().optional(),
  active: z.boolean().default(true),
});
export type CustomerCreateRequest = z.infer<typeof customerCreateRequestSchema>;

export const customerUpdateRequestSchema = customerCreateRequestSchema.partial();
export type CustomerUpdateRequest = z.infer<typeof customerUpdateRequestSchema>;

export const customerResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  country: z.string(),
  vatNumber: z.string().nullable(),
  // VIES fields: popolati dal modulo Fatture quando implementeremo SOAP check
  viesVerifiedAt: z.string().nullable(),
  viesValid: z.boolean().nullable(),
  address: addressSchema.nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomerResponse = z.infer<typeof customerResponseSchema>;

export const customerListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  search: z.string().trim().min(1).max(100).optional(),
  country: countryCodeSchema.optional(),
  active: z.coerce.boolean().optional(),
});
export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;

export const customerListResponseSchema = z.object({
  items: z.array(customerResponseSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type CustomerListResponse = z.infer<typeof customerListResponseSchema>;
